import { useTranslation } from 'react-i18next';
import BucketDots from './BucketDots';

const fmtNumber = (n) => (n || 0).toLocaleString();

const UserAvatar = ({ user }) =>
  user?.avatar ? (
    <img
      src={user.avatar}
      alt=""
      className="h-9 w-9 shrink-0 rounded-full object-cover bg-[#F1F5F9]"
    />
  ) : (
    <div className="h-9 w-9 shrink-0 rounded-full bg-[#EEF2FF] flex items-center justify-center text-[#3B5BDB] text-[13px] font-semibold">
      {(user?.name || user?.username || '?').charAt(0).toUpperCase()}
    </div>
  );

/**
 * Level 1 of the drawer: WHO engaged with this item, ranked by total events.
 *
 * This exists because the flat timeline answered the wrong question first. On TIRI especially,
 * Reach has no dedup — one fan swiping back and forth writes dozens of identical view rows — so
 * a page of the raw log was mostly noise and an admin had to read all of it to learn the one
 * thing they wanted: how many people touched this, and did any of them actually do something.
 * That is now the first screen, and the log is one tap underneath it.
 */
const EventUserList = ({
  users,
  total,
  loading,
  page,
  pageSize,
  onPageChange,
  onUserClick,
  fmtRelative
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="py-4 space-y-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="h-9 w-9 rounded-full bg-[#F1F5F9]" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-1/2 bg-[#F1F5F9] rounded" />
              <div className="h-2.5 w-1/3 bg-[#F1F5F9] rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!users?.length) {
    return (
      <div className="py-10 text-center">
        <p className="text-[13px] font-medium text-[#334155]">
          {t('admin.insights.drawer.noPeople')}
        </p>
        <p className="text-[12px] text-[#94A3B8] mt-1">{t('admin.insights.drawer.noPeopleSub')}</p>
      </div>
    );
  }

  const hasPrev = page > 0;
  const hasNext = (page + 1) * pageSize < total;

  return (
    <>
      <div className="divide-y divide-[#F1F5F9]">
        {users.map((row) => {
          // An event outlives the account that made it, so a row can arrive with no fan. It still
          // carries real counts, so it is shown rather than hidden — just not as a link to a
          // timeline that would have nobody to filter on.
          const id = row.user?._id;
          const name =
            row.user?.name || row.user?.username || t('admin.insights.drawer.deletedUser');

          const Row = id ? 'button' : 'div';

          return (
            <Row
              key={id || `deleted-${row.total}-${row.lastAt}`}
              {...(id
                ? {
                    onClick: () => onUserClick(row),
                    className:
                      'w-full flex items-center gap-3 py-3 text-left hover:bg-[#F8FAFC] transition-colors'
                  }
                : { className: 'w-full flex items-center gap-3 py-3 opacity-60' })}
            >
              <UserAvatar user={row.user} />

              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-[#0F172A] truncate">{name}</p>
                <BucketDots row={row} />
                <p className="text-[11px] text-[#94A3B8] mt-0.5">
                  {t('admin.insights.drawer.lastActive', { time: fmtRelative(row.lastAt) })}
                </p>
              </div>

              <div className="shrink-0 text-right flex items-center gap-1.5">
                <span className="text-[15px] font-semibold text-[#0F172A] tabular-nums">
                  {fmtNumber(row.total)}
                </span>
                {id && <span className="text-[#CBD5E1] text-[14px]">›</span>}
              </div>
            </Row>
          );
        })}
      </div>

      {(hasPrev || hasNext) && (
        <div className="flex items-center justify-between py-3">
          <p className="text-[12px] text-[#94A3B8]">
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {fmtNumber(total)}
          </p>
          <div className="flex gap-2">
            <button
              disabled={!hasPrev}
              onClick={() => onPageChange(page - 1)}
              className="h-8 px-3 rounded-lg text-[12px] font-medium border border-[#CBD5E1] bg-white text-[#334155] hover:border-[#94A3B8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            <button
              disabled={!hasNext}
              onClick={() => onPageChange(page + 1)}
              className="h-8 px-3 rounded-lg text-[12px] font-medium border border-[#CBD5E1] bg-white text-[#334155] hover:border-[#94A3B8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EventUserList;
